import type { Layout } from '../data/types';
import type { Ring, Vec2 } from '../geo/types';
import { centroid, pointInRing } from '../geo/polygon';
import { DISCOVERIES } from './discoveries';
import { nearestOn } from './reach';
import type { DiscoveryPlace } from './types';

/** Where a found place is marked on the painting: its centre when that lies in its outline,
 *  otherwise the outline's point nearest the centre, so a crooked place is still marked on it. */
export function markPoint(footprint: Ring): Vec2 {
  const middle = centroid(footprint);
  return pointInRing(middle, footprint) ? middle : nearestOn(footprint, middle).point;
}

/** The discoveries on this city's own landmarks. A discovery of a landmark the layout does not
 *  hold, or holds without a source, is refused: nothing is shown that the layout cannot back. */
export function discoveryPlaces(layout: Layout): readonly DiscoveryPlace[] {
  const titles = new Map(layout.meta.sources.map((source) => [source.id, source.title]));
  return DISCOVERIES.map((discovery) => {
    const landmark = layout.landmarks.find((one) => one.id === discovery.id);
    if (!landmark) throw new Error(`Discovery of an unknown landmark: ${discovery.id}`);
    const sources = landmark.sourceRefs.map((ref) => {
      const title = titles.get(ref);
      if (!title) throw new Error(`Landmark ${landmark.id} cites an unknown source: ${ref}`);
      return title;
    });
    if (sources.length === 0) throw new Error(`Discovery without a source: ${discovery.id}`);
    return {
      ...discovery,
      footprint: landmark.footprint,
      confidence: landmark.confidence,
      sources,
    };
  });
}
